/**
 * "My work" hooks (P268.2).
 *
 * `useMyWork` -- the unified inbox; polls every 60s so the header badge
 * stays fresh without a live push (the notification-bell cadence).
 * `useUsersSearch` -- debounced typeahead backing the UserPicker.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type { MyWork, UsersSearch, WorkAction } from "shared/api/types";

export const workKeys = {
  mine: ["my-work"] as const,
  usersSearch: (q: string) => ["users-search", q] as const,
};

/**
 * @param paused While a sign panel is open. B1 -- the 60s poll
 * reordered the list under the cursor as other people acted, and two
 * signers signed the row that had moved into the place they were
 * aiming at. A signature is not something to be aimed at a moving
 * target, so the list holds still until the panel closes.
 */
export function useMyWork(paused = false): UseQueryResult<MyWork, ApiError> {
  return useQuery({
    queryKey: workKeys.mine,
    queryFn: () => api.get<MyWork>("/v1/me/work"),
    // Match the notification-bell cadence -- 60s, no live push at MVP.
    refetchInterval: paused ? false : 60_000,
  });
}

export function useUsersSearch(q: string): UseQueryResult<UsersSearch, ApiError> {
  const query = q.trim();
  return useQuery({
    queryKey: workKeys.usersSearch(query),
    queryFn: () =>
      api.get<UsersSearch>(`/v1/me/users-search?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}

/**
 * Perform one work item's action from the inbox.
 *
 * The acceptance check is that the CRO clears a morning of sign-offs
 * "without leaving /my-work". The composer hands over the exact
 * endpoint, so this knows nothing about the six ceremony shapes behind
 * it -- and cannot drift from them.
 */
/** Fill the endpoint's own signature field names from the session. */
function signatureBody(
  action: WorkAction,
  name: string,
  email: string,
): Record<string, string> {
  const [nameField, emailField] = action.signature_fields;
  const out: Record<string, string> = {};
  if (nameField) out[nameField] = name;
  if (emailField) out[emailField] = email;
  return out;
}

export function usePerformWorkAction(): UseMutationResult<
  unknown,
  ApiError,
  { action: WorkAction; name: string; email: string; note?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ action, name, email, note }) =>
      api.request<unknown>(action.method, action.href, {
        ...action.body,
        ...signatureBody(action, name, email),
        // Only where the endpoint keeps it -- see `note_field`.
        ...(action.note_field && note ? { [action.note_field]: note } : {}),
      }),
    onSuccess: () => {
      // C2 -- a sign-off changes governance state that a dozen
      // surfaces read: admission status, MVB membership, the tolerance
      // in force, the story hub, the owner's estate. Invalidating only
      // the inbox left an owner who had just signed reading "not yet
      // admitted" on his own home. Which surfaces care is not
      // knowable from here, and listing them would go stale the next
      // time one is added, so everything is refetched: this happens
      // once, on a deliberate human act.
      void qc.invalidateQueries();
    },
  });
}
